import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Save, Shield, FileText, Trash2, 
  AlertTriangle, CheckCircle, Clock, XCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pages'); // pages, deletion_requests
  
  // Pages légales
  const [legalPages, setLegalPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  const [pageContent, setPageContent] = useState({ title: '', content: '' });
  
  // Demandes de suppression
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionStats, setDeletionStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [processingRequest, setProcessingRequest] = useState(null);
  const [processNotes, setProcessNotes] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    try {
      const [pagesRes, requestsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/legal`, getAuthHeaders()),
        axios.get(`${API}/admin/data-deletion-requests`, getAuthHeaders()),
        axios.get(`${API}/admin/data-deletion-requests/stats`, getAuthHeaders())
      ]);
      
      setLegalPages(pagesRes.data);
      setDeletionRequests(requestsRes.data);
      setDeletionStats(statsRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditPage = (page) => {
    setEditingPage(page.page_type);
    setPageContent({
      title: page.title || '',
      content: page.content || ''
    });
  };

  const handleSavePage = async () => {
    if (!pageContent.title || !pageContent.content) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/legal/${editingPage}`,
        pageContent,
        getAuthHeaders()
      );
      toast.success('Page mise à jour avec succès');
      setEditingPage(null);
      fetchData();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleProcessRequest = async (requestId, status) => {
    try {
      await axios.patch(
        `${API}/admin/data-deletion-requests/${requestId}`,
        { status, notes: processNotes },
        getAuthHeaders()
      );
      
      const statusMessages = {
        processing: 'Demande marquée en traitement',
        completed: 'Données supprimées avec succès',
        rejected: 'Demande rejetée'
      };
      
      toast.success(statusMessages[status]);
      setProcessingRequest(null);
      setProcessNotes('');
      fetchData();
    } catch (error) {
      console.error('Erreur traitement:', error);
      toast.error('Erreur lors du traitement de la demande');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: '#FFF3E0', color: '#E65100', icon: Clock, label: 'En attente' },
      processing: { bg: '#E3F2FD', color: '#1565C0', icon: AlertTriangle, label: 'En cours' },
      completed: { bg: '#E8F5E9', color: '#2E7D32', icon: CheckCircle, label: 'Complété' },
      rejected: { bg: '#FFEBEE', color: '#C62828', icon: XCircle, label: 'Rejeté' }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span 
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
        style={{ background: badge.bg, color: badge.color }}
      >
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  const getPageTypeLabel = (type) => {
    const labels = {
      privacy_policy: 'Politique de confidentialité',
      terms_of_service: 'Conditions d\'utilisation'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#335C2E' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 rounded-lg hover:bg-white/10 text-white"
              >
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Shield size={28} />
                  Pages Légales & RGPD
                </h1>
                <p className="text-white/80 text-sm">Gérez vos contenus légaux et les demandes de suppression</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('pages')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'pages' 
                ? 'text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'pages' ? { background: '#335C2E' } : {}}
          >
            <FileText size={18} className="inline mr-2" />
            Pages légales
          </button>
          <button
            onClick={() => setActiveTab('deletion_requests')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'deletion_requests' 
                ? 'text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'deletion_requests' ? { background: '#335C2E' } : {}}
          >
            <Trash2 size={18} />
            Demandes de suppression
            {deletionStats?.pending > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs" style={{ background: '#DC3545', color: 'white' }}>
                {deletionStats.pending}
              </span>
            )}
          </button>
        </div>

        {/* Pages légales */}
        {activeTab === 'pages' && (
          <div className="space-y-6">
            {editingPage ? (
              /* Formulaire d'édition */
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold" style={{ color: '#335C2E' }}>
                    Modifier : {getPageTypeLabel(editingPage)}
                  </h2>
                  <button
                    onClick={() => setEditingPage(null)}
                    className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Titre de la page</label>
                    <input
                      type="text"
                      value={pageContent.title}
                      onChange={(e) => setPageContent({ ...pageContent, title: e.target.value })}
                      className="w-full p-3 border rounded-lg"
                      placeholder="Ex: Politique de confidentialité"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Contenu (HTML)</label>
                    <textarea
                      value={pageContent.content}
                      onChange={(e) => setPageContent({ ...pageContent, content: e.target.value })}
                      className="w-full p-3 border rounded-lg font-mono text-sm"
                      rows="20"
                      placeholder="<h2>Titre</h2><p>Contenu...</p>"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Vous pouvez utiliser des balises HTML : &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSavePage}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-3 rounded-lg text-white hover:opacity-90 disabled:opacity-50"
                      style={{ background: '#335C2E' }}
                    >
                      <Save size={18} />
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    <button
                      onClick={() => window.open(`/legal/${editingPage}`, '_blank')}
                      className="flex items-center gap-2 px-6 py-3 rounded-lg border hover:bg-gray-50"
                    >
                      <Eye size={18} />
                      Prévisualiser
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Liste des pages */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {legalPages.map((page) => (
                  <div key={page.page_type} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                          {getPageTypeLabel(page.page_type)}
                        </h3>
                        {page.last_updated && (
                          <p className="text-sm text-gray-500 mt-1">
                            Dernière mise à jour: {new Date(page.last_updated).toLocaleDateString('fr-CA')}
                            {page.updated_by && ` par ${page.updated_by}`}
                          </p>
                        )}
                      </div>
                      <FileText size={24} style={{ color: '#335C2E' }} />
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                      {page.content ? (
                        <span className="text-green-600">✓ Contenu personnalisé</span>
                      ) : (
                        <span className="text-orange-600">⚠ Contenu par défaut</span>
                      )}
                    </p>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditPage(page)}
                        className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90"
                        style={{ background: '#335C2E' }}
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => window.open(`/legal/${page.page_type}`, '_blank')}
                        className="px-4 py-2 rounded-lg border hover:bg-gray-50"
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Demandes de suppression */}
        {activeTab === 'deletion_requests' && (
          <div className="space-y-6">
            {/* Stats */}
            {deletionStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#335C2E' }}>{deletionStats.total}</p>
                  <p className="text-sm text-gray-500">Total</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-2xl font-bold text-orange-600">{deletionStats.pending}</p>
                  <p className="text-sm text-gray-500">En attente</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">{deletionStats.processing}</p>
                  <p className="text-sm text-gray-500">En cours</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-2xl font-bold text-green-600">{deletionStats.completed}</p>
                  <p className="text-sm text-gray-500">Complétées</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-2xl font-bold text-red-600">{deletionStats.rejected}</p>
                  <p className="text-sm text-gray-500">Rejetées</p>
                </div>
              </div>
            )}

            {/* Filtre */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <label className="block text-sm font-medium mb-2">Filtrer par statut</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="p-2 border rounded-lg"
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="processing">En cours</option>
                <option value="completed">Complétées</option>
                <option value="rejected">Rejetées</option>
              </select>
            </div>

            {/* Liste des demandes */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: '#F5F5F5' }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Demandeur</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Raison</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Statut</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletionRequests
                      .filter(r => !filterStatus || r.status === filterStatus)
                      .map((request) => (
                        <tr key={request.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium">{request.full_name}</td>
                          <td className="px-4 py-3 text-sm">{request.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {request.reason || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(request.requested_at).toLocaleDateString('fr-CA')}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(request.status)}
                          </td>
                          <td className="px-4 py-3">
                            {request.status === 'pending' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setProcessingRequest(request)}
                                  className="px-3 py-1 rounded text-sm text-white"
                                  style={{ background: '#335C2E' }}
                                >
                                  Traiter
                                </button>
                              </div>
                            )}
                            {request.status === 'processing' && (
                              <button
                                onClick={() => setProcessingRequest(request)}
                                className="px-3 py-1 rounded text-sm bg-green-600 text-white"
                              >
                                Finaliser
                              </button>
                            )}
                            {(request.status === 'completed' || request.status === 'rejected') && (
                              <span className="text-sm text-gray-500">
                                {request.processed_by && `Par ${request.processed_by}`}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {deletionRequests.length === 0 && (
                  <div className="text-center py-12">
                    <Trash2 size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">Aucune demande de suppression</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal de traitement */}
            {processingRequest && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                  <h3 className="text-lg font-semibold mb-4">Traiter la demande</h3>
                  
                  <div className="mb-4 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                    <p><strong>Nom:</strong> {processingRequest.full_name}</p>
                    <p><strong>Email:</strong> {processingRequest.email}</p>
                    {processingRequest.reason && (
                      <p><strong>Raison:</strong> {processingRequest.reason}</p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
                    <textarea
                      value={processNotes}
                      onChange={(e) => setProcessNotes(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      rows="3"
                      placeholder="Notes sur le traitement..."
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    {processingRequest.status === 'pending' && (
                      <button
                        onClick={() => handleProcessRequest(processingRequest.id, 'processing')}
                        className="w-full py-2 rounded-lg text-white"
                        style={{ background: '#2196F3' }}
                      >
                        Marquer en cours de traitement
                      </button>
                    )}
                    <button
                      onClick={() => handleProcessRequest(processingRequest.id, 'completed')}
                      className="w-full py-2 rounded-lg text-white"
                      style={{ background: '#4CAF50' }}
                    >
                      ✓ Supprimer les données et finaliser
                    </button>
                    <button
                      onClick={() => handleProcessRequest(processingRequest.id, 'rejected')}
                      className="w-full py-2 rounded-lg text-white"
                      style={{ background: '#DC3545' }}
                    >
                      ✕ Rejeter la demande
                    </button>
                    <button
                      onClick={() => {
                        setProcessingRequest(null);
                        setProcessNotes('');
                      }}
                      className="w-full py-2 rounded-lg border hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-4">
                    ⚠️ La suppression supprimera définitivement : contacts CRM, soumissions, rendez-vous et anonymisera les commentaires de blog.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalSettings;
